/* =============================================================================
   BULLION — product.js  (product template only)
   Gallery, variant picker, sticky add-to-cart, recently viewed.
   ========================================================================== */
(() => {
  'use strict';
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const MVS = window.MVS;

  /* ---------- Gallery ---------- */
  class Gallery extends HTMLElement {
    connectedCallback() {
      this.track = $('.product__track', this);
      this.slides = $$('.product__slide', this);
      this.thumbs = $$('.product__thumb', this);
      this.dots = $$('.product__dots button', this);
      if (!this.track || !this.slides.length) return;
      this.index = 0;
      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.go(i)));
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.go(i)));
      $('[data-gallery-prev]', this)?.addEventListener('click', () => this.go(this.index - 1));
      $('[data-gallery-next]', this)?.addEventListener('click', () => this.go(this.index + 1));
      this.track.addEventListener('scroll', () => { clearTimeout(this._t); this._t = setTimeout(() => this.syncFromScroll(), 80); }, { passive: true });
      // zoom on click (images only)
      this.slides.forEach(slide => {
        if (!slide.classList.contains('product__slide--zoom')) return;
        slide.addEventListener('click', (e) => {
          const zoomed = slide.classList.toggle('is-zoomed');
          if (zoomed) this.setZoomOrigin(slide, e);
        });
        slide.addEventListener('mousemove', (e) => { if (slide.classList.contains('is-zoomed')) this.setZoomOrigin(slide, e); });
        slide.addEventListener('mouseleave', () => slide.classList.remove('is-zoomed'));
      });
      this.addEventListener('keydown', (e) => { if (e.key === 'ArrowRight') this.go(this.index + 1); if (e.key === 'ArrowLeft') this.go(this.index - 1); });
    }
    setZoomOrigin(slide, e) {
      const r = slide.getBoundingClientRect();
      slide.style.setProperty('--zx', `${((e.clientX - r.left) / r.width * 100).toFixed(1)}%`);
      slide.style.setProperty('--zy', `${((e.clientY - r.top) / r.height * 100).toFixed(1)}%`);
    }
    go(i) {
      i = (i + this.slides.length) % this.slides.length;
      this.slides.forEach(s => s.classList.remove('is-zoomed'));
      this.track.scrollTo({ left: this.slides[i].offsetLeft, behavior: 'smooth' });
      this.setActive(i);
      this.pauseMedia(i);
    }
    goToMedia(mediaId) { const i = this.slides.findIndex(s => s.dataset.mediaId === String(mediaId)); if (i > -1) this.go(i); }
    syncFromScroll() { const i = Math.round(this.track.scrollLeft / this.track.clientWidth); if (i !== this.index) { this.setActive(i); this.pauseMedia(i); } }
    setActive(i) {
      this.index = i;
      this.thumbs.forEach((t, k) => t.setAttribute('aria-current', String(k === i)));
      this.dots.forEach((d, k) => d.setAttribute('aria-current', String(k === i)));
      const th = this.thumbs[i]; th && th.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
    pauseMedia(active) { this.slides.forEach((s, k) => { if (k === active) return; $$('video', s).forEach(v => v.pause()); }); }
  }
  customElements.define('product-gallery', Gallery);

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = $('[type="application/json"]', this);
      if (!json) return;
      this.variants = JSON.parse(json.textContent);
      this.sectionId = this.dataset.section;
      this.optionCount = parseInt(this.dataset.options || '1', 10);
      this.addEventListener('change', () => this.onChange());
      this.updateAvailability();
    }
    get selected() {
      return Array.from({ length: this.optionCount }, (_, i) => {
        const checked = $(`input[name="option${i + 1}"]:checked`, this) || $(`select[name="option${i + 1}"]`, this);
        return checked ? checked.value : null;
      });
    }
    onChange() {
      const sel = this.selected;
      const variant = this.variants.find(v => v.options.every((o, i) => o === sel[i]));
      this.updateAvailability();
      this.updateOutputs(sel);
      this.apply(variant);
    }
    updateOutputs(sel) { $$('[data-option-output]', this).forEach((o, i) => o.textContent = sel[i] || ''); }
    updateAvailability() {
      const sel = this.selected;
      $$('input[type="radio"]', this).forEach(input => {
        const i = parseInt(input.name.replace('option', ''), 10) - 1;
        const probe = sel.slice(); probe[i] = input.value;
        const match = this.variants.find(v => v.options.every((o, k) => k === i ? o === input.value : (probe[k] == null || o === probe[k])));
        const label = input.nextElementSibling;
        label && label.classList.toggle('is-unavailable', !(match && match.available));
      });
    }
    apply(variant) {
      const root = document.getElementById(`shopify-section-${this.sectionId}`) || document;
      const idInput = $('form [name="id"]', root);
      const btn = $('form [type="submit"]', root);
      const btnText = $('[data-atc-text]', btn || root);
      const priceEl = $('[data-product-price]', root);
      const stickyPrice = $('[data-sticky-price]');
      const stickyBtn = $('[data-sticky-atc]');
      const inv = $('[data-inventory]', root);
      if (!variant) {
        if (btn) { btn.setAttribute('aria-disabled', 'true'); btnText && (btnText.textContent = MVS.strings.unavailable); }
        return;
      }
      if (idInput) { idInput.value = variant.id; idInput.dispatchEvent(new Event('change', { bubbles: true })); }
      if (btn) {
        btn.toggleAttribute('disabled', !variant.available);
        btn.setAttribute('aria-disabled', String(!variant.available));
        btnText && (btnText.textContent = variant.available ? MVS.strings.addToCart : MVS.strings.soldOut);
      }
      if (stickyBtn) { stickyBtn.toggleAttribute('disabled', !variant.available); $('[data-atc-text]', stickyBtn).textContent = variant.available ? MVS.strings.addToCart : MVS.strings.soldOut; }
      const priceHtml = this.priceHtml(variant);
      priceEl && (priceEl.innerHTML = priceHtml);
      stickyPrice && (stickyPrice.innerHTML = priceHtml);
      if (inv) {
        const qty = variant.inventory_quantity;
        if (typeof qty === 'number' && variant.inventory_management) {
          inv.hidden = false;
          inv.className = 'product__inventory' + (qty <= 0 ? ' product__inventory--out' : qty <= parseInt(inv.dataset.lowThreshold || '5', 10) ? ' product__inventory--low' : '');
          inv.textContent = qty <= 0 ? (variant.available ? inv.dataset.backorder : MVS.strings.soldOut) : (qty <= parseInt(inv.dataset.lowThreshold || '5', 10) ? inv.dataset.low.replace('__count__', qty) : inv.dataset.instock);
        } else inv.hidden = true;
      }
      if (variant.featured_media) { const g = $('product-gallery', root); g && g.goToMedia(variant.featured_media.id); }
      const url = new URL(window.location.href); url.searchParams.set('variant', variant.id); window.history.replaceState({}, '', url);
      $$('[data-sku]', root).forEach(el => { el.textContent = variant.sku || ''; el.closest('[data-sku-row]') && (el.closest('[data-sku-row]').hidden = !variant.sku); });
    }
    priceHtml(v) {
      const onSale = v.compare_at_price && v.compare_at_price > v.price;
      return `<span class="price${onSale ? ' price--on-sale' : ''}"><span class="price__current">${MVS.formatMoney(v.price)}</span>${onSale ? `<s class="price__compare">${MVS.formatMoney(v.compare_at_price)}</s>` : ''}</span>`;
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------- Sticky add to cart (mobile) ---------- */
  const sticky = $('.sticky-atc');
  const anchor = $('[data-atc-anchor]');
  if (sticky && anchor && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(([en]) => sticky.classList.toggle('is-visible', !en.isIntersecting && en.boundingClientRect.top < 0), { threshold: 0 });
    io.observe(anchor);
    $('[data-sticky-atc]', sticky)?.addEventListener('click', () => { const form = $('product-form form'); form && form.requestSubmit(); });
  }

  /* ---------- Recently viewed ---------- */
  const current = document.body.dataset.productHandle;
  const rv = $('[data-recently-viewed]');
  if (rv) {
    const handles = MVS.getRecent().filter(h => h !== current).slice(0, parseInt(rv.dataset.limit || '4', 10));
    if (!handles.length) rv.closest('.shopify-section')?.remove();
    else {
      const q = handles.map(h => `handle:${h}`).join(' OR ');
      fetch(`${MVS.routes.search}?q=${encodeURIComponent(q)}&type=product&section_id=${rv.dataset.section}`)
        .then(r => r.text()).then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const grid = $('[data-recently-viewed-grid]', doc);
          const target = $('[data-recently-viewed-grid]', rv);
          if (grid && target && grid.children.length) {
            // keep the visitor's viewing order
            const cards = Array.from(grid.children);
            cards.sort((a, b) => handles.indexOf(a.dataset.handle) - handles.indexOf(b.dataset.handle));
            target.innerHTML = ''; cards.forEach(c => target.appendChild(c));
            rv.hidden = false; MVS.bindGlow && MVS.bindGlow(rv); MVS.syncWishlist && MVS.syncWishlist(rv);
          } else rv.closest('.shopify-section')?.remove();
        }).catch(() => rv.closest('.shopify-section')?.remove());
    }
  }
  if (current) MVS.trackRecent(current);

  /* ---------- Recommendations (Section Rendering API) ---------- */
  const recs = $('[data-recommendations]');
  if (recs) {
    const load = () => fetch(`${MVS.routes.recommendations}?section_id=${recs.dataset.section}&product_id=${recs.dataset.productId}&limit=${recs.dataset.limit || 4}&intent=related`)
      .then(r => r.text()).then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = $('[data-recommendations]', doc);
        if (inner && inner.querySelector('.product-card')) { recs.innerHTML = inner.innerHTML; MVS.bindGlow && MVS.bindGlow(recs); MVS.observeReveal && MVS.observeReveal(recs); MVS.syncWishlist && MVS.syncWishlist(recs); }
        else recs.closest('.shopify-section')?.remove();
      }).catch(() => {});
    if ('IntersectionObserver' in window) { const io = new IntersectionObserver(([en]) => { if (en.isIntersecting) { load(); io.disconnect(); } }, { rootMargin: '400px' }); io.observe(recs); }
    else load();
  }
})();
